import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Minus, Plus, Banknote, Truck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { imgSrc } from "@/components/Catalog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [others, setOthers] = useState([]);
  const [qty, setQtyLocal] = useState(1);
  const [notFound, setNotFound] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setQtyLocal(1);
    window.scrollTo(0, 0);
    axios.get(`${API}/products/${id}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
    axios.get(`${API}/products`).then((r) => setOthers(r.data.filter((p) => p.id !== id).slice(0, 3))).catch(() => {});
  }, [id]);

  const addToCart = () => {
    for (let i = 0; i < qty; i++) addItem(product);
    toast.success(`${product.name} añadido a la cesta`);
  };

  return (
    <main className="bg-noir min-h-screen" data-testid="product-page">
      <Navbar />
      <div className="pt-32 pb-24 px-6 max-w-6xl mx-auto">
        <Link to="/" data-testid="product-back-link" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-neutral-500 hover:text-gold transition-colors mb-12">
          <ArrowLeft size={14} /> Volver a la colección
        </Link>

        {notFound && (
          <p className="text-center text-neutral-500 font-light py-24" data-testid="product-not-found">Este perfume no está disponible.</p>
        )}

        {!product && !notFound && (
          <p className="text-center text-gold font-serif-lux tracking-[0.3em] uppercase py-24 animate-pulse">Cargando...</p>
        )}

        {product && (
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="relative aspect-[3/4] overflow-hidden border border-white/10 bg-noir-soft grain-overlay"
            >
              <img src={imgSrc(product.image)} alt={product.name} className="w-full h-full object-cover" data-testid="product-image" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.15, ease: "easeOut" }}
              className="lg:pt-8"
            >
              <span className="font-script-lux text-gold text-3xl">Parfums d'Exception</span>
              <h1 className="font-serif-lux text-5xl sm:text-6xl tracking-[0.12em] uppercase text-white mt-4 leading-tight" data-testid="product-name">
                {product.name}
              </h1>
              <div className="w-16 h-px bg-gold my-8" />
              <p className="text-neutral-400 font-light leading-relaxed max-w-md" data-testid="product-description">{product.description}</p>
              <p className="font-serif-lux text-4xl text-gold mt-10" data-testid="product-price">{product.price.toFixed(2)} €</p>

              <div className="flex items-center gap-6 mt-10">
                <div className="flex items-center border border-white/15">
                  <button data-testid="product-qty-minus" onClick={() => setQtyLocal((q) => Math.max(1, q - 1))} className="w-11 h-11 flex items-center justify-center text-neutral-400 hover:text-gold transition-colors" aria-label="Reducir cantidad">
                    <Minus size={14} />
                  </button>
                  <span className="w-10 text-center text-white" data-testid="product-qty">{qty}</span>
                  <button data-testid="product-qty-plus" onClick={() => setQtyLocal((q) => Math.min(20, q + 1))} className="w-11 h-11 flex items-center justify-center text-neutral-400 hover:text-gold transition-colors" aria-label="Aumentar cantidad">
                    <Plus size={14} />
                  </button>
                </div>
                <button
                  data-testid="product-add-to-cart"
                  onClick={addToCart}
                  className="group relative flex-1 bg-gold text-black text-xs uppercase tracking-[0.25em] py-4 overflow-hidden"
                >
                  <span className="relative z-10 transition-colors duration-500 group-hover:text-gold">Añadir a la cesta</span>
                  <span className="absolute inset-0 bg-black translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
                </button>
              </div>

              <div className="mt-10 space-y-4">
                <div className="flex items-center gap-4 text-neutral-400 text-sm font-light">
                  <Banknote size={18} className="text-gold shrink-0" /> Pago contra reembolso: pagas en efectivo al recibir.
                </div>
                <div className="flex items-center gap-4 text-neutral-400 text-sm font-light">
                  <Truck size={18} className="text-gold shrink-0" /> Entrega personal a domicilio.
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {others.length > 0 && (
          <div className="mt-32">
            <h2 className="font-serif-lux text-3xl tracking-[0.2em] uppercase text-white text-center mb-14">También te puede gustar</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-10">
              {others.map((p) => (
                <Link key={p.id} to={`/producto/${p.id}`} data-testid={`related-product-${p.id}`} className="group block">
                  <div className="aspect-[3/4] overflow-hidden border border-white/5 bg-noir-soft">
                    <img src={imgSrc(p.image)} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
                  </div>
                  <h3 className="font-serif-lux text-xl tracking-[0.15em] uppercase text-white text-center mt-5 group-hover:text-gold transition-colors">{p.name}</h3>
                  <p className="text-gold font-serif-lux text-center mt-2">{p.price.toFixed(2)} €</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
}
